/**
 * Build Menu - allows player to construct buildings
 * Enhanced with traditional Eastern aesthetic UI design
 */

import { useGameStore } from '@/state/gameStore';
import { BUILDING_STATS } from '@/game/factions/FactionData';
import { injectSharedStyles } from '@/ui/styles/UIStyles';
import { BUILDING_ICONS, COIN_STACK_SMALL, UI_IMAGES } from '@/ui/styles/UIIcons';
import type { BuildingType } from '@/types';

export interface BuildMenuCallbacks {
  onSelectBuilding: (buildingType: BuildingType) => void;
  onCancel: () => void;
}

export class BuildMenu {
  private container: HTMLElement;
  private callbacks: BuildMenuCallbacks;
  private unsubscribe: (() => void) | null = null;

  constructor(callbacks: BuildMenuCallbacks) {
    this.callbacks = callbacks;
    injectSharedStyles();

    this.container = document.createElement('div');
    this.container.id = 'build-menu';
    this.container.innerHTML = `
      <style>
        #build-menu {
          position: fixed;
          left: 50%;
          transform: translateX(-50%);
          bottom: 16px;
          z-index: 20;
          font: 13px/1.4 'Noto Serif SC', 'SimSun', serif;
          color: #f5e6d3;
          user-select: none;
        }

        .build-menu-wrapper {
          background:
            linear-gradient(145deg, rgba(74,55,40,0.85) 0%, rgba(45,31,21,0.9) 100%),
            url('/images/ui/elements/wood.webp');
          background-size: cover;
          background-position: center;
          border: 3px solid #8b6914;
          border-radius: 8px;
          box-shadow:
            inset 0 2px 4px rgba(255,220,150,0.15),
            inset 0 -3px 6px rgba(0,0,0,0.4),
            0 6px 20px rgba(0,0,0,0.6);
          padding: 0;
          overflow: hidden;
          position: relative;
        }

        /* Dragon corner ornaments - Using sprite sheet */
        .build-menu-corner {
          position: absolute;
          width: 32px;
          height: 32px;
          background-image: url('/images/ui/decorations/corner-ornaments.png');
          background-size: 200% 200%;
          z-index: 3;
          filter: drop-shadow(0 2px 3px rgba(0,0,0,0.4));
          pointer-events: none;
        }
        .build-menu-corner.tl { top: -5px; left: -5px; background-position: 0% 0%; }
        .build-menu-corner.tr { top: -5px; right: -5px; background-position: 100% 0%; }
        .build-menu-corner.bl { bottom: -5px; left: -5px; background-position: 0% 100%; }
        .build-menu-corner.br { bottom: -5px; right: -5px; background-position: 100% 100%; }

        .build-menu-title-bar {
          background:
            linear-gradient(180deg, rgba(107,68,35,0.9) 0%, rgba(74,47,23,0.95) 100%),
            url('/images/ui/elements/frame.webp');
          background-size: cover;
          background-position: center;
          border-bottom: 2px solid #8b6914;
          padding: 8px 12px;
          text-align: center;
          position: relative;
        }

        .build-menu-title-bar::after {
          content: '';
          position: absolute;
          bottom: -3px;
          left: 30px;
          right: 30px;
          height: 1px;
          background: linear-gradient(90deg,
            transparent 0%,
            rgba(212,175,55,0.6) 30%,
            rgba(212,175,55,0.8) 50%,
            rgba(212,175,55,0.6) 70%,
            transparent 100%
          );
        }

        .build-menu-title {
          font: 700 13px/1 'Noto Serif SC', 'SimSun', serif;
          color: #ffd35a;
          text-shadow: 0 1px 2px rgba(0,0,0,0.5), 0 0 8px rgba(255,180,0,0.2);
          letter-spacing: 2px;
        }

        #build-items {
          display: flex;
          gap: 6px;
          padding: 12px;
          background:
            linear-gradient(to bottom, rgba(45,31,21,0.85) 0%, rgba(45,31,21,0.9) 100%),
            url('/images/ui/elements/parchment.webp');
          background-size: cover;
          background-position: center;
        }

        #build-menu .build-item {
          width: 72px;
          background: linear-gradient(180deg, #5c4033 0%, #3d2a1c 100%);
          border: 2px solid #6b4423;
          border-radius: 6px;
          padding: 10px 6px 8px;
          text-align: center;
          cursor: pointer;
          transition: all 0.15s ease;
          position: relative;
          overflow: hidden;
        }

        #build-menu .build-item::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 50%;
          background: linear-gradient(180deg, rgba(255,255,255,0.06) 0%, transparent 100%);
          pointer-events: none;
        }

        #build-menu .build-item:hover:not(.disabled) {
          border-color: #d4af37;
          transform: translateY(-3px);
          box-shadow: 0 6px 12px rgba(0,0,0,0.4), 0 0 8px rgba(212,175,55,0.3);
        }

        #build-menu .build-item.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        #build-menu .build-item.selected {
          border-color: #d4af37;
          background: linear-gradient(180deg, #6b4e3a 0%, #4a3525 100%);
          box-shadow: 0 0 12px rgba(212,175,55,0.5);
        }

        .build-icon {
          width: 42px;
          height: 42px;
          margin: 0 auto 6px;
          background: linear-gradient(180deg, #4a3728 0%, #2d1f15 100%);
          border: 2px solid #5c4033;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .build-icon svg {
          width: 32px;
          height: 32px;
        }

        #build-menu .build-item:not(.disabled) .build-icon {
          border-color: #8b6914;
          background: linear-gradient(180deg, #5c4033 0%, #3d2a1c 100%);
        }

        #build-menu .build-name {
          font-size: 11px;
          color: #c4a77d;
          margin-bottom: 4px;
        }

        #build-menu .build-item.disabled .build-name {
          color: #9e8b6e;
        }

        #build-menu .build-cost {
          font-size: 12px;
          font-weight: 700;
          color: #ffd700;
          text-shadow: 0 0 4px rgba(255,215,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 3px;
        }

        #build-menu .build-cost .coin-icon {
          width: 14px;
          height: 14px;
          display: inline-flex;
        }

        #build-menu .build-item.disabled .build-cost {
          color: #8b6914;
          text-shadow: none;
        }

        /* Tooltip */
        #build-menu .build-item::after {
          content: attr(data-tooltip);
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(45,31,21,0.95);
          border: 1px solid #8b6914;
          border-radius: 4px;
          padding: 6px 10px;
          font-size: 11px;
          color: #f5e6d3;
          white-space: nowrap;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.2s;
          margin-bottom: 6px;
          z-index: 100;
          box-shadow: 0 4px 8px rgba(0,0,0,0.4);
        }

        #build-menu .build-item:hover::after {
          opacity: 1;
        }

        .build-hint {
          background: rgba(0,0,0,0.2);
          border-top: 1px solid rgba(139,105,20,0.4);
          padding: 8px 12px;
          font-size: 11px;
          color: #9e8b6e;
          text-align: center;
          display: none;
        }

        .build-hint.visible {
          display: block;
        }

        .demolish-hint {
          background: rgba(100,30,30,0.3);
          border-top: 1px solid rgba(180,80,80,0.4);
          padding: 8px 12px;
          font-size: 11px;
          color: #e8a0a0;
          text-align: center;
          display: none;
        }

        .demolish-hint.visible {
          display: block;
        }

        .demolish-hint .building-name {
          color: #f5d0a0;
          font-weight: bold;
        }

        .demolish-hint .key-hint {
          color: #c0b090;
          margin-left: 8px;
        }

        /* Compact layout for small viewports (desktop 720p) */
        @media (max-height: 800px) {
          #build-menu {
            bottom: 6px;
          }
          .build-menu-wrapper {
            border-width: 2px;
          }
          .build-menu-corner {
            width: 20px;
            height: 20px;
          }
          .build-menu-title-bar {
            padding: 3px 10px;
          }
          .build-menu-title {
            font-size: 10px;
            letter-spacing: 1px;
          }
          #build-items {
            gap: 3px;
            padding: 4px 6px;
          }
          #build-menu .build-item {
            width: 52px;
            padding: 4px 3px 3px;
          }
          .build-icon {
            width: 28px;
            height: 28px;
            margin: 0 auto 2px;
            border-width: 1px;
          }
          .build-icon svg {
            width: 20px;
            height: 20px;
          }
          #build-menu .build-name {
            font-size: 9px;
            margin-bottom: 1px;
          }
          #build-menu .build-cost {
            font-size: 9px;
          }
          #build-menu .build-cost .coin-icon {
            width: 10px;
            height: 10px;
          }
          .build-hint, .demolish-hint {
            padding: 3px 8px;
            font-size: 9px;
          }
        }
      </style>
      <div class="build-menu-wrapper">
        <div class="build-menu-corner tl"></div>
        <div class="build-menu-corner tr"></div>
        <div class="build-menu-corner bl"></div>
        <div class="build-menu-corner br"></div>

        <div class="build-menu-title-bar">
          <div class="build-menu-title">建造</div>
        </div>

        <div id="build-items"></div>
        <div class="build-hint" id="build-hint">点击地图放置建筑 <span style="color:#9e8b6e;margin-left:8px;">(右键或ESC取消)</span></div>
        <div class="demolish-hint" id="demolish-hint">已选中: <span class="building-name" id="selected-building-name"></span><span class="key-hint">按 Delete 拆除</span></div>
      </div>
    `;

    document.body.appendChild(this.container);

    this.setupItems();
    this.setupEvents();
    this.setupSubscription();
  }

  private setupItems(): void {
    const itemsContainer = this.container.querySelector('#build-items')!;
    const buildableTypes: BuildingType[] = ['barracks', 'archery', 'spearcamp', 'stable', 'smithy', 'temple', 'tower', 'wall', 'mine', 'farm', 'market'];

    for (const type of buildableTypes) {
      const stats = BUILDING_STATS[type];
      const item = document.createElement('div');
      item.className = 'build-item';
      item.dataset.type = type;
      item.dataset.tooltip = this.getBuildingDesc(type);
      item.innerHTML = `
        <div class="build-icon">${this.getBuildingIcon(type)}</div>
        <div class="build-name">${this.getBuildingName(type)}</div>
        <div class="build-cost"><span class="coin-icon">${COIN_STACK_SMALL}</span>${stats.cost}g</div>
      `;
      item.addEventListener('click', () => this.selectBuilding(type));
      itemsContainer.appendChild(item);
    }
  }

  private getBuildingIcon(type: BuildingType): string {
    const imagePath = UI_IMAGES.buildings[type as keyof typeof UI_IMAGES.buildings];
    if (imagePath) {
      return `<img src="${imagePath}" alt="${type}" style="width:100%;height:100%;object-fit:contain;" />`;
    }
    return BUILDING_ICONS[type as keyof typeof BUILDING_ICONS] || BUILDING_ICONS.barracks;
  }

  private getBuildingName(type: BuildingType): string {
    const names: Record<string, string> = {
      barracks: '兵营',
      archery: '射箭场',
      spearcamp: '枪营',
      tower: '箭塔',
      wall: '城墙',
      mine: '矿场',
      farm: '农场',
      market: '市场',
      stable: '马厩',
      smithy: '铁匠铺',
      temple: '道观',
    };
    return names[type] || type;
  }

  private getBuildingDesc(type: BuildingType): string {
    const stats = BUILDING_STATS[type];
    const sizeStr = stats?.size ? `[${stats.size.width}x${stats.size.depth}] ` : '';
    const descs: Record<string, string> = {
      barracks: '训练剑士单位',
      archery: '训练弓兵单位',
      spearcamp: '训练枪兵单位',
      tower: '攻击敌人',
      wall: '防御城墙，自动连接',
      mine: '产出 +50 金/分钟',
      farm: '产出 +30 金/分钟',
      market: '提升收入',
      stable: '训练骑兵单位',
      smithy: '训练盾兵单位',
      temple: '训练道士单位',
    };
    return sizeStr + (descs[type] || '');
  }

  private selectBuilding(type: BuildingType): void {
    const state = useGameStore.getState();
    const stats = BUILDING_STATS[type];

    if (state.playerGold < stats.cost) {
      return;
    }

    useGameStore.getState().setSelectedBuildingType(type);
    this.callbacks.onSelectBuilding(type);
  }

  private setupEvents(): void {
    // ESC key handling is done in Game.ts to avoid conflicts
    // No separate handler needed here
  }

  private cancelSelection(): void {
    useGameStore.getState().setSelectedBuildingType(null);
    const hint = this.container.querySelector('#build-hint');
    if (hint) hint.classList.remove('visible');
  }

  private setupSubscription(): void {
    this.unsubscribe = useGameStore.subscribe((state) => {
      this.updateItems(state.playerGold, state.selectedBuildingType);

      // Show hint when a building type is selected for placement
      const buildHint = this.container.querySelector('#build-hint');
      if (buildHint) {
        buildHint.classList.toggle('visible', !!state.selectedBuildingType);
      }

      // Show demolish hint when a building entity is selected
      const demolishHint = this.container.querySelector('#demolish-hint');
      const buildingNameEl = this.container.querySelector('#selected-building-name');
      if (demolishHint && buildingNameEl) {
        demolishHint.classList.toggle('visible', !!state.selectedBuildingEntity);
        if (state.selectedBuildingName) {
          buildingNameEl.textContent = state.selectedBuildingName;
        }
      }
    });
  }

  private updateItems(gold: number, selectedType: string | null): void {
    const items = this.container.querySelectorAll('.build-item');
    items.forEach((item) => {
      const type = (item as HTMLElement).dataset.type as BuildingType;
      const stats = BUILDING_STATS[type];

      item.classList.toggle('disabled', gold < stats.cost);
      item.classList.toggle('selected', type === selectedType);
    });
  }

  show(): void {
    // Menu is always visible now
  }

  hide(): void {
    // Menu is always visible, just cancel selection
    this.cancelSelection();
  }

  toggle(): void {
    // Menu is always visible now, no toggle needed
  }

  isVisible(): boolean {
    return true; // Always visible
  }

  dispose(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    this.container.remove();
  }
}
